export interface PdfPageViewRotationSource {
  rotation?: number;
  viewport?: {
    rotation?: number;
  };
}

export interface SerializedAnnotationObject {
  scaleX: number;
  scaleY: number;
  top: number;
  left: number;
  angle: number;
}

interface SerializedTextAnnotationObject extends SerializedAnnotationObject {
  type: "i-text" | "text" | "textbox";
  fontSize: number;
}

const isTextObject = (obj: SerializedAnnotationObject): obj is SerializedTextAnnotationObject => {
  const o = obj as unknown as Record<string, unknown>;
  return (o.type === "i-text" || o.type === "text" || o.type === "textbox") && typeof o.fontSize === "number";
};

export interface SerializedAnnotationCanvas<TObject extends SerializedAnnotationObject = SerializedAnnotationObject> {
  objects: TObject[];
  pageWidth?: number;
  pageHeight?: number;
}

const normalizeRotation = (rotation: number): number => ((rotation % 360) + 360) % 360;

/**
 * The coordinate space stored annotations live in.
 *
 * Stored annotations are normalized to scale 1 and to the page's unrotated orientation, so the stored
 * page dimensions describe their space exactly. Records saved before those dimensions were persisted
 * fall back to the caller supplied size.
 */
export const resolveStoredCanvasSize = (
  serializedCanvas: SerializedAnnotationCanvas,
  fallbackSize: { width: number; height: number }
): { width: number; height: number } => {
  const { pageWidth, pageHeight } = serializedCanvas;
  const hasStoredSize = Number.isFinite(pageWidth) && pageWidth > 0 && Number.isFinite(pageHeight) && pageHeight > 0;

  return hasStoredSize ? { width: pageWidth, height: pageHeight } : fallbackSize;
};

export const getPdfPageViewRotation = (source: PdfPageViewRotationSource): number => {
  return normalizeRotation(source.viewport?.rotation ?? source.rotation ?? 0);
};

export interface TransformSerializedAnnotationCanvasOptions {
  /**
   * Overwrite pageWidth/pageHeight with the size the objects end up in.
   *
   * Only correct when normalizing for storage, where `canvasSize` is the live canvas the objects come
   * from. When scaling stored annotations back up for display the stored dimensions already describe
   * the page and must be left untouched - rewriting them multiplies them by the zoom level a second
   * time, and the backend then renders the annotations into a canvas that is too large for them.
   *
   * @default true
   */
  writePageDimensions?: boolean;
}

export const transformSerializedAnnotationCanvas = <
  TCanvas extends SerializedAnnotationCanvas<TObject>,
  TObject extends SerializedAnnotationObject
>(
  serializedCanvas: TCanvas,
  deltaScale: number,
  deltaRotation: number,
  canvasSize: { width: number; height: number },
  options: TransformSerializedAnnotationCanvasOptions = {}
): TCanvas => {
  const { writePageDimensions = true } = options;
  const serializedCanvasSize: { width: number; height: number } = {
    width: canvasSize.width * deltaScale,
    height: canvasSize.height * deltaScale,
  };
  const normalizedDeltaRotation = normalizeRotation(deltaRotation);
  const shouldSwapPageDimensions = normalizedDeltaRotation === 90 || normalizedDeltaRotation === 270;

  // Never transform in place: the source is the stored annotation record, which is shared with the
  // copy that gets persisted for every page of the file.
  const transformedCanvas: TCanvas = {
    ...serializedCanvas,
    objects: serializedCanvas.objects.map((object) => ({ ...object })),
  };

  if (writePageDimensions) {
    // The backend renders into the PDF page's unrotated coordinate space.
    transformedCanvas.pageWidth = shouldSwapPageDimensions ? serializedCanvasSize.height : serializedCanvasSize.width;
    transformedCanvas.pageHeight = shouldSwapPageDimensions ? serializedCanvasSize.width : serializedCanvasSize.height;
  }

  transformedCanvas.objects.forEach((object) => {
    if (deltaScale !== 1) {
      if (isTextObject(object)) {
        const prevScaleX = object.scaleX;
        object.fontSize = object.fontSize * prevScaleX * deltaScale;
        object.scaleX = 1;
        object.scaleY = prevScaleX === 0 ? object.scaleY : object.scaleY / prevScaleX;
      } else {
        object.scaleX *= deltaScale;
        object.scaleY *= deltaScale;
      }
      object.top *= deltaScale;
      object.left *= deltaScale;
    }
    if (normalizedDeltaRotation !== 0) {
      let newTop: number, newLeft: number, newAngle: number;
      switch (normalizedDeltaRotation) {
        case 90:
          newTop = object.left;
          newLeft = serializedCanvasSize.height - object.top;
          newAngle = (object.angle + 90) % 360;
          break;
        case 180:
          newTop = serializedCanvasSize.height - object.top;
          newLeft = serializedCanvasSize.width - object.left;
          newAngle = (object.angle + 180) % 360;
          break;
        case 270:
          newTop = serializedCanvasSize.width - object.left;
          newLeft = object.top;
          newAngle = (object.angle + 270) % 360;
          break;
        default:
          return;
      }
      object.top = newTop;
      object.left = newLeft;
      object.angle = newAngle;
    }
  });

  return transformedCanvas;
};
